import {
  DndContext,
  DndProvider,
  useDragDropManager,
} from '@nosferatu500/react-dnd'
import { HTML5Backend } from '@nosferatu500/react-dnd-html5-backend'
import {
  gridNavigation,
  isKeyboardDrag,
  type KeyboardBackendOptions,
  useDragDropAnnounce,
  withKeyboard,
} from '@nosferatu500/react-dnd-keyboard-backend'
import React, {
  type ReactNode,
  useCallback,
  useDeferredValue,
  useEffect,
  useEffectEvent,
  useId,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react'
import { VList, type VListHandle } from 'virtua'
import NodeRendererDefault from './node-renderer-default'
import PlaceholderRendererDefault from './placeholder-renderer-default'
import TreeNode from './tree-node'
import TreePlaceholder from './tree-placeholder'
import type {
  GetNodeKeyFunction,
  GetTreeItemChildrenFn,
  TreeItem,
  TreeKey,
  UnknownNodeData,
} from './types'
import {
  type DepthAnnouncement,
  defaultTreeAnnouncements,
  type MoveAnnouncement,
  type TreeAnnouncements,
} from './utils/announcements'
import { classnames } from './utils/classnames'
import {
  defaultGetNodeKey,
  defaultSearchMethod,
} from './utils/default-handlers'
import {
  type EndDragPhase,
  type TreeDndHandlers,
  wrapPlaceholder,
  wrapSource,
  wrapTarget,
} from './utils/dnd-manager'
import { slideRows } from './utils/generic-utils'
import {
  createKeyboardDragController,
  treeOnNavigate,
} from './utils/keyboard-drag'
import { rowIdentity } from './utils/node-identity'
import {
  type FlatDataItem,
  changeNodeAtPath,
  find,
  getDescendantCount,
  getFlatDataFromTree,
  insertNode,
  removeNode,
  toggleExpandedForAll,
  walk,
} from './utils/tree-data-utils'
import { useIsomorphicLayoutEffect } from './utils/use-isomorphic-layout-effect'
import './react-sortable-tree.css'

type SearchParams<TData> = {
  node: TreeItem<TData>
  path: number[]
  treeIndex: number
  searchQuery: string
}

type SearchFinishCallbackParams<TData> = {
  node: TreeItem<TData>
  path: number[]
  treeIndex: number
}[]

export type GenerateNodePropsParams<TData = UnknownNodeData> = {
  node: TreeItem<TData>
  path: number[]
  treeIndex: number
  lowerSiblingCounts: number[]
  isSearchMatch: boolean
  isSearchFocus: boolean
}

type ShouldCopyOnOutsideDropParams<TData> = {
  node: TreeItem<TData>
  prevPath: number[]
  prevTreeIndex: number
}

type OnMoveNodeParams<TData> = {
  treeData: TreeItem<TData>[]
  node: TreeItem<TData>
  nextParentNode: TreeItem<TData> | null
  prevPath: number[]
  prevTreeIndex: number
  nextPath?: number[]
  nextTreeIndex?: number
}

export type CanDropParams<TData = UnknownNodeData> = {
  node: TreeItem<TData>
  prevPath: number[]
  prevParent?: TreeItem<TData>
  prevTreeIndex: number
  nextPath: number[]
  nextParent?: TreeItem<TData>
  nextTreeIndex: number
}

type OnVisibilityToggleParams<TData> = {
  treeData: TreeItem<TData>[]
  node: TreeItem<TData>
  expanded: boolean
  path: number[]
}

type OnDragStateChangedParams<TData> = {
  isDragging: boolean
  draggedNode: TreeItem<TData> | undefined
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyRenderer = React.ComponentType<any>

/**
 * The `theme` prop's shape: renderer slots and layout defaults a theme can set,
 * each overridable by the matching prop on the component itself.
 */
export type ThemeProps = {
  style?: React.CSSProperties
  innerStyle?: React.CSSProperties
  scaffoldBlockPxWidth?: number
  treeNodeRenderer?: AnyRenderer
  nodeContentRenderer?: AnyRenderer
  placeholderRenderer?: AnyRenderer
  dndType?: string
}

export type ReactSortableTreeProps<TData = UnknownNodeData> = {
  children?: ReactNode
  dragDropManager?: {
    getMonitor: () => unknown
  }
  treeData: TreeItem<TData>[]
  style?: React.CSSProperties
  className?: string
  virtuaRef?: React.RefObject<VListHandle>
  innerStyle?: React.CSSProperties
  scaffoldBlockPxWidth?: number
  maxDepth?: number
  searchMethod?: (params: SearchParams<TData>) => boolean
  searchQuery?: string
  searchFocusOffset?: number
  searchFinishCallback?: (params: SearchFinishCallbackParams<TData>) => void
  generateNodeProps?: (
    params: GenerateNodePropsParams<TData>
  ) => Record<string, unknown>
  treeNodeRenderer?: AnyRenderer
  nodeContentRenderer?: AnyRenderer
  placeholderRenderer?: AnyRenderer
  theme?: ThemeProps
  rowHeight?:
    | number
    | ((treeIndex: number, node: TreeItem<TData>, path: number[]) => number)
  getNodeKey?: GetNodeKeyFunction<TData>
  onChange: (treeData: TreeItem<TData>[]) => void
  onMoveNode?: (params: OnMoveNodeParams<TData>) => void
  /**
   * Persists a completed move, and is *awaited*.
   *
   * `onChange` and `onMoveNode` both fire as soon as the move commits and cannot
   * fail — they say "this happened". `onDrop` says "make this stick", so
   * returning a promise from it gives the tree the three states a save really
   * has instead of one:
   *
   * - while it is pending, the moved row's renderer receives `isSettling`, and
   *   the drag is over as far as the pointer is concerned;
   * - if it resolves, the move is announced to screen readers as done;
   * - if it **rejects**, the tree reverts to the data from before the drop,
   *   emits `onChange` with it, and announces the failure. Without this the tree
   *   would keep showing a move the consumer never persisted.
   *
   * `signal` aborts when the drop can no longer affect anything — today, when a
   * new drag starts. Forward it to `fetch`; an `AbortError` after it fires is
   * treated as the tree's own doing and neither reverts nor announces.
   *
   * The rejection reason also reaches `monitor.getDropError()` and the
   * environment's uncaught-error handling, so a failure is never silent even
   * with nothing rendering it.
   */
  onDrop?: (
    params: OnMoveNodeParams<TData>,
    signal: AbortSignal
  ) => Promise<void> | void
  canDrag?: boolean | ((params: GenerateNodePropsParams<TData>) => boolean)
  canDrop?: (params: CanDropParams<TData>) => boolean
  canNodeHaveChildren?: (node: TreeItem<TData>) => boolean
  shouldCopyOnOutsideDrop?:
    ((params: ShouldCopyOnOutsideDropParams<TData>) => boolean) | boolean
  onVisibilityToggle?: (params: OnVisibilityToggleParams<TData>) => void
  dndType?: string
  onDragStateChanged?: (params: OnDragStateChangedParams<TData>) => void
  onlyExpandSearchedNodes?: boolean
  rowDirection?: string
  loadCollapsedLazyChildren?: boolean
  /**
   * Accessible name for the tree. A `role="tree"` needs one; supply this or
   * `aria-labelledby`.
   */
  'aria-label'?: string
  'aria-labelledby'?: string
  /**
   * Set to false to opt out of the built-in arrow-key navigation, for example
   * when the surrounding app binds those keys itself. ARIA roles and the roving
   * tabindex stay in place either way.
   */
  keyboardNavigation?: boolean
  /**
   * Replaces the strings the tree speaks to a screen reader — where a node
   * landed, and what depth an arrow key produced. Override any subset.
   *
   * This is half of the localisation story. The other half belongs to the
   * keyboard backend, which narrates pick-up, movement and cancellation, and is
   * replaced through `withTreeKeyboard`'s `announcements` option. The split is
   * where the knowledge is: the backend cannot know that a row is now a child of
   * "Documents", and the tree cannot know which row the backend is hovering.
   *
   * ```tsx
   * <SortableTree
   *   announcements={{
   *     moved: ({ node, depth }) => `${node.title} : profondeur ${depth}.`,
   *   }}
   * />
   * ```
   */
  announcements?: TreeAnnouncements<TData>
}

const DEFAULT_SCAFFOLD_BLOCK_PX_WIDTH = 44
const DEFAULT_ROW_HEIGHT = 62
const alwaysTrue = () => true

/** Children the node can actually reveal, as opposed to a lazy-loader function. */
const hasRevealableChildren = <TData,>(node: TreeItem<TData>): boolean =>
  typeof node.children === 'function' ||
  (Array.isArray(node.children) && node.children.length > 0)

/**
 * Restores horizontal scrolling, which virtua's row wrapper would otherwise
 * suppress.
 *
 * A deeply nested row is wider than the viewport — the scaffold indents it by
 * `scaffoldBlockPxWidth` per level and `.rst__nodeContent` is absolutely
 * positioned past that — and the tree has to scroll sideways to reach it, or the
 * row's buttons are unreachable.
 *
 * Virtua sizes its inner container on the scroll axis only: for a vertical list
 * the height is the total row height and the width is `100%`. Nothing across the
 * axis is ever wider than the scroller, so a wide row is pure *overflow* — and
 * the wrapper's `contain: layout` stops that overflow counting towards any
 * ancestor's scrollable area. Measured: the wrapper's own `scrollWidth` grew to
 * 654px while the scroller stayed at 620px, so `overflow-x: auto` had nothing to
 * scroll and `contain: strict` on the scroller clipped the row instead.
 *
 * Dropping `layout` from the containment is what fixes it, and it has to be done
 * here: virtua sets that `contain` inline, which a stylesheet cannot override
 * without `!important`, and this component *is* the wrapper it styles.
 *
 * `isolation: isolate` puts back the one thing `contain: layout` also did — make
 * each row a stacking context. Without it `.rst__rowLandingPad::before`
 * (`z-index: -1`) escapes behind an ancestor and the drop highlight disappears,
 * and the `z-index: 3` drag lines stop being scoped to their own row.
 *
 * `style` is spread first so this only replaces the two properties and inherits
 * whatever else virtua sets, including any it adds later.
 *
 * Also `role="none"`: left as a plain `<div>` this wrapper sits between
 * `role="tree"` and `role="treeitem"` and breaks the ownership the ARIA tree
 * pattern requires.
 */
const PresentationalItem = ({
  style,
  children,
  ref,
}: {
  style: React.CSSProperties
  children: ReactNode
  index: number
  ref?: React.Ref<HTMLDivElement>
}) => (
  <div
    ref={ref}
    role="none"
    style={{ ...style, contain: 'style', isolation: 'isolate' }}>
    {children}
  </div>
)

/** Keys handled by the tree, so anything else passes through untouched. */
const NAVIGATION_KEYS = new Set([
  'ArrowDown',
  'ArrowUp',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
])

/** True when the key event came from a control that needs the keystroke. */
const isFromTextEntry = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false
  return (
    target.isContentEditable ||
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
  )
}

/**
 * The promise dnd-core is holding a settling phase open on, split so the effect
 * that has the move params can finish what the drop handler started.
 */
interface PendingSettle {
  resolve: () => void
  reject: (reason: unknown) => void
  /** Aborted once the drop can no longer affect anything. */
  signal: AbortSignal
}

interface ReactSortableTreeState<TData> {
  draggingTreeData?: TreeItem<TData>[]
  draggedNode?: TreeItem<TData>
  draggedMinimumTreeIndex?: number
  draggedDepth?: number
  searchMatches: Array<{ path: number[]; treeIndex: number }>
  searchFocusTreeIndex?: number
  dragging: boolean
  treeData: TreeItem<TData>[]
  ignoreOneTreeUpdate: boolean
}

interface DropResult<TData> {
  node: TreeItem<TData>
  path: number[]
  treeIndex: number
  treeId: string
  minimumTreeIndex: number
  depth: number
}

type SearchConfig<TData> = Pick<
  ReactSortableTreeProps<TData>,
  | 'onChange'
  | 'searchFinishCallback'
  | 'searchQuery'
  | 'searchMethod'
  | 'searchFocusOffset'
  | 'onlyExpandSearchedNodes'
> & { getNodeKey: GetNodeKeyFunction<TData> }

const performSearch = <TData,>(
  props: SearchConfig<TData>,
  treeData: TreeItem<TData>[],
  seekIndex: boolean,
  expand: boolean,
  singleSearch: boolean
): {
  searchMatches: Array<{ path: number[]; treeIndex: number }>
  searchFocusTreeIndex?: number
  newTreeData?: TreeItem<TData>[]
} => {
  const {
    onChange,
    getNodeKey,
    searchFinishCallback,
    searchQuery,
    searchMethod,
    searchFocusOffset,
    onlyExpandSearchedNodes,
  } = props

  // Skip search if no conditions are specified
  if (!searchQuery && !searchMethod) {
    if (searchFinishCallback) {
      searchFinishCallback([])
    }

    return { searchMatches: [] }
  }

  // if onlyExpandSearchedNodes collapse the tree and search
  const { treeData: expandedTreeData, matches: searchMatches } = find({
    getNodeKey,
    treeData: onlyExpandSearchedNodes
      ? toggleExpandedForAll({
          treeData,
          expanded: false,
        })
      : treeData,
    searchQuery,
    searchMethod: searchMethod || defaultSearchMethod,
    searchFocusOffset,
    expandAllMatchPaths: expand && !singleSearch,
    expandFocusMatchPaths: !!expand,
  })

  // Update the tree with data leaving all paths leading to matching nodes open
  let newTreeData: TreeItem<TData>[] | undefined
  if (expand) {
    newTreeData = expandedTreeData
    onChange(expandedTreeData)
  }

  if (searchFinishCallback) {
    searchFinishCallback(searchMatches)
  }

  // Indexing rather than range-checking: the old `searchFocusOffset <
  // searchMatches.length` test let a negative offset through, and
  // `searchMatches[-1].treeIndex` threw.
  const focusedMatch =
    seekIndex && searchFocusOffset !== undefined
      ? searchMatches[searchFocusOffset]
      : undefined
  const searchFocusTreeIndex: number | undefined = focusedMatch?.treeIndex

  return { searchMatches, searchFocusTreeIndex, newTreeData }
}

type LazyChildrenConfig<TData> = Pick<
  ReactSortableTreeProps<TData>,
  'onChange' | 'loadCollapsedLazyChildren'
> & { getNodeKey: GetNodeKeyFunction<TData> }

const isThenable = <TData,>(
  value: unknown
): value is PromiseLike<TreeItem<TData>[] | undefined> =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as PromiseLike<unknown>).then === 'function'

// Load any children in the tree that are given by a function
// calls the onChange callback on the new treeData
const loadLazyChildren = <TData,>(
  props: LazyChildrenConfig<TData>,
  treeData: TreeItem<TData>[]
) => {
  walk({
    treeData,
    getNodeKey: props.getNodeKey,
    callback: ({ node, path, lowerSiblingCounts, treeIndex }) => {
      // Only nodes whose children are given by a function, and that are either
      //  expanded or set to load even before expansion.
      if (
        typeof node.children !== 'function' ||
        !(node.expanded || props.loadCollapsedLazyChildren)
      ) {
        return
      }

      // Append the loaded data
      const applyChildren = (childrenArray: TreeItem<TData>[]) =>
        props.onChange(
          changeNodeAtPath({
            treeData,
            path,
            newNode: ({ node: oldNode }: { node: TreeItem<TData> }) =>
              // Only replace the old node if it's the one we set off to find children
              //  for in the first place
              oldNode === node
                ? {
                    ...oldNode,
                    children: childrenArray,
                  }
                : oldNode,
            getNodeKey: props.getNodeKey,
          })
        )

      // Call the children fetching function
      const result = (node.children as GetTreeItemChildrenFn<TData>)({
        node,
        path,
        lowerSiblingCounts,
        treeIndex,
      })

      if (Array.isArray(result)) {
        applyChildren(result)
      } else if (isThenable(result)) {
        // Rejections are deliberately not swallowed: an unhandled rejection is
        // visible in the console and catchable by the consumer, whereas
        // silently dropping it would leave the node stuck on its spinner with
        // no diagnostic at all.
        void result.then((childrenArray) =>
          // A loader that resolves to nothing is a typeless caller's mistake.
          // Leave the node pending rather than writing `children: undefined`,
          // which would drop both the loader and anything already there.
          childrenArray ? applyChildren(childrenArray) : undefined
        )
      }
    },
  })
}

/**
 * What every move announcement is built from, so the three of them cannot drift
 * apart. Undefined for a move with no landing path — a node that left the tree,
 * which the tree has nothing to say about.
 */
const describeMove = <TData,>(
  params: OnMoveNodeParams<TData>
): MoveAnnouncement<TData> | undefined => {
  const { node, nextPath, nextParentNode } = params
  if (!nextPath) return undefined
  // `nextParentNode` is `null` at the top level; normalised so the public
  // announcement type has one absent value rather than two.
  return {
    node,
    parentNode: nextParentNode ?? undefined,
    depth: nextPath.length,
  }
}

/**
 * Resolves one move message.
 *
 * Falling back per key rather than wholesale is the point: overriding `moved`
 * alone must not silence the other two, which is what a single
 * `announcements ?? defaults` would do.
 */
const moveMessage = <TData,>(
  announcements: TreeAnnouncements<TData> | undefined,
  key: 'moving' | 'moved' | 'moveFailed',
  params: MoveAnnouncement<TData>
): string => (announcements?.[key] ?? defaultTreeAnnouncements[key])(params)

const depthMessage = <TData,>(
  announcements: TreeAnnouncements<TData> | undefined,
  params: DepthAnnouncement
): string => (announcements?.depth ?? defaultTreeAnnouncements.depth)(params)

interface TreeRowProps<TData> {
  /**
   * The flattened row. A single object rather than its parts spread out, because
   * `rows` is memoized: while the data is unchanged this is the same reference
   * every render, and the memo below passes.
   */
  row: FlatDataItem<TData>
  /** The row above, for the depth maths a drag does. Stable for the same reason. */
  prevRow: FlatDataItem<TData> | undefined
  listIndex: number
  /**
   * Passed as a boolean rather than as `activeRowIndex`, which would differ for
   * every row on every focus move and re-render the whole visible window to
   * change two rows.
   */
  isActiveRow: boolean
  isSearchMatch: boolean
  isSearchFocus: boolean
  swapFrom: number | undefined
  swapDepth: number | undefined
  swapLength: number | undefined
  rootNodeCount: number
  scaffoldBlockPxWidth: number
  treeId: string
  rowDirection: string
  rowHeight:
    | number
    | ((treeIndex: number, node: TreeItem<TData>, path: number[]) => number)
  toggleChildrenVisibility: (params: {
    node: TreeItem<TData>
    path: number[]
  }) => void
  generateNodeProps?: (
    params: GenerateNodePropsParams<TData>
  ) => Record<string, unknown>
  canDrag: boolean | ((params: GenerateNodePropsParams<TData>) => boolean)
  TreeNodeRenderer: AnyRenderer
  NodeContentRenderer: AnyRenderer
}

/**
 * One row of the tree, memoized.
 *
 * The memo boundary has to be *here*, around the composition, rather than on the
 * row renderer itself. A row is
 * `<TreeNodeRenderer …><NodeContentRenderer …/></TreeNodeRenderer>`, so the row
 * renderer's `children` is a fresh element on every render and a shallow
 * comparison can never pass — `React.memo` was measured directly on `TreeNode`
 * once and removed again for exactly that reason, as pure overhead plus a
 * misleading signal that rows were memoized. Building the composition *inside*
 * the memoized component puts that freshness inside the boundary, where it costs
 * nothing.
 *
 * What this buys: a re-render that does not change the data no longer re-renders
 * every visible row. Moving focus with an arrow key is the clearest case — it
 * changes `isActiveRow` for two rows and used to re-render the whole window.
 * Rows are virtualized, so the win is bounded by the window rather than the tree.
 *
 * Every prop is therefore a value or a stable reference. Two are the consumer's
 * to keep stable, `generateNodeProps` and a function `rowHeight`, and both are
 * *called here* rather than in the parent: an inline arrow then costs its own
 * rows a re-render instead of defeating the memo for all of them.
 */
const TreeRowComponent = <TData,>({
  row,
  prevRow,
  listIndex,
  isActiveRow,
  isSearchMatch,
  isSearchFocus,
  swapFrom,
  swapDepth,
  swapLength,
  rootNodeCount,
  scaffoldBlockPxWidth,
  treeId,
  rowDirection,
  rowHeight,
  toggleChildrenVisibility,
  generateNodeProps,
  canDrag,
  TreeNodeRenderer,
  NodeContentRenderer,
}: TreeRowProps<TData>) => {
  const { node, parentNode, path, lowerSiblingCounts, treeIndex } = row

  const callbackParams = {
    node,
    parentNode,
    path,
    lowerSiblingCounts,
    treeIndex,
    isSearchMatch,
    isSearchFocus,
  }
  const nodeProps = generateNodeProps
    ? generateNodeProps(callbackParams as GenerateNodePropsParams<TData>)
    : {}
  const rowCanDrag =
    typeof canDrag === 'function'
      ? canDrag(callbackParams as GenerateNodePropsParams<TData>)
      : canDrag

  const sharedProps = {
    treeIndex,
    scaffoldBlockPxWidth,
    node,
    path,
    treeId,
    rowDirection,
  }

  // ARIA tree semantics. The DOM is flat because the list is virtualized, so
  // depth and sibling position have to be stated explicitly rather than
  // inferred from nesting.
  const siblingCount = Array.isArray(parentNode?.children)
    ? parentNode.children.length
    : rootNodeCount
  const lowerSiblings = lowerSiblingCounts.at(-1) ?? 0

  return (
    <TreeNodeRenderer
      rowHeight={rowHeight}
      listIndex={listIndex}
      getPrevRow={() => prevRow}
      lowerSiblingCounts={lowerSiblingCounts}
      swapFrom={swapFrom}
      swapLength={swapLength}
      swapDepth={swapDepth}
      role="treeitem"
      aria-level={path.length}
      aria-setsize={siblingCount}
      aria-posinset={siblingCount - lowerSiblings}
      aria-expanded={
        hasRevealableChildren(node) ? node.expanded === true : undefined
      }
      tabIndex={isActiveRow ? 0 : -1}
      data-rst-row={listIndex}
      {...sharedProps}>
      <NodeContentRenderer
        parentNode={parentNode}
        isSearchMatch={isSearchMatch}
        isSearchFocus={isSearchFocus}
        canDrag={rowCanDrag}
        // The keyboard backend makes every drag handle focusable, which would
        // put one tab stop per visible row inside a tree that the ARIA pattern
        // says must have exactly one. It defers to a `tabindex` that is already
        // there, so the handle joins the roving tabindex instead: reachable by
        // Tab from its own row, invisible to Tab from anywhere else.
        isActiveRow={isActiveRow}
        toggleChildrenVisibility={toggleChildrenVisibility}
        {...sharedProps}
        {...nodeProps}
      />
    </TreeNodeRenderer>
  )
}

/*
 * `React.memo` erases type parameters — its overloads resolve `P` from a
 * `FunctionComponent<P>`, so a generic component comes back inferred at
 * `unknown` and every row prop stops matching. Asserting the original signature
 * restores it, and is sound because `memo` changes when a component is called,
 * never how.
 */
const TreeRow = React.memo(TreeRowComponent) as typeof TreeRowComponent

const ReactSortableTreeInner = <TData,>(
  props: Readonly<ReactSortableTreeProps<TData>>
) => {
  const {
    treeData: treeDataProp,
    onChange,
    onMoveNode,
    onDrop,
    onVisibilityToggle,
    onDragStateChanged,
    searchFinishCallback,
    searchQuery,
    searchMethod,
    searchFocusOffset,
    onlyExpandSearchedNodes = false,
    generateNodeProps,
    getNodeKey = defaultGetNodeKey,
    canDrag = true,
    canDrop,
    canNodeHaveChildren = alwaysTrue,
    shouldCopyOnOutsideDrop = false,
    maxDepth,
    loadCollapsedLazyChildren,
    className = '',
    rowDirection = 'ltr',
    virtuaRef,
    theme,
    style: styleProp,
    innerStyle: innerStyleProp,
    scaffoldBlockPxWidth: scaffoldBlockPxWidthProp,
    rowHeight = DEFAULT_ROW_HEIGHT,
    nodeContentRenderer: nodeContentRendererProp,
    placeholderRenderer: placeholderRendererProp,
    treeNodeRenderer: treeNodeRendererProp,
    dndType: dndTypeProp,
    keyboardNavigation = true,
    announcements,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
  } = props

  // Whether row keys can come from `getNodeKey`. The default one is positional,
  // so it cannot serve as a React key — see the `rowKey` comment below.
  const usesDefaultNodeKey = getNodeKey === defaultGetNodeKey

  // Theme fallbacks. These are plain expressions rather than a memoized merged
  // object: a merged object would be a fresh reference on every render, and
  // anything memoized against it — including the wrapped DnD components — would
  // rebuild along with it.
  const NodeContent: AnyRenderer =
    nodeContentRendererProp ?? theme?.nodeContentRenderer ?? NodeRendererDefault
  const PlaceholderContent: AnyRenderer =
    placeholderRendererProp ??
    theme?.placeholderRenderer ??
    PlaceholderRendererDefault
  const TreeNodeRendererBase: AnyRenderer =
    treeNodeRendererProp ?? theme?.treeNodeRenderer ?? TreeNode
  const scaffoldBlockPxWidth =
    scaffoldBlockPxWidthProp ??
    theme?.scaffoldBlockPxWidth ??
    DEFAULT_SCAFFOLD_BLOCK_PX_WIDTH

  const themeStyle = theme?.style
  const themeInnerStyle = theme?.innerStyle
  const style = useMemo(
    () => ({ ...themeStyle, ...styleProp }),
    [themeStyle, styleProp]
  )
  const innerStyle = useMemo(
    () => ({ ...themeInnerStyle, ...innerStyleProp }),
    [themeInnerStyle, innerStyleProp]
  )

  // Stable, unique tree id
  const generatedTreeId = useId()
  const treeId = `rst__${generatedTreeId}`
  const dndType = dndTypeProp ?? theme?.dndType ?? treeId

  // Non-blocking search
  const [, startSearchTransition] = useTransition()
  const deferredSearchQuery = useDeferredValue(searchQuery)
  const deferredSearchFocusOffset = useDeferredValue(searchFocusOffset)

  // Refs
  const internalListRef = useRef<VListHandle>(null)
  const listRef = virtuaRef ?? internalListRef
  const prevTreeDataRef = useRef<TreeItem<TData>[]>(treeDataProp)
  const prevDeferredSearchQueryRef = useRef(deferredSearchQuery)
  const prevDeferredSearchFocusOffsetRef = useRef(deferredSearchFocusOffset)
  const prevDraggingRef = useRef(false)
  const isInitialMountRef = useRef(true)

  // Refs for pending callbacks (to avoid calling onChange during setState)
  const pendingOnChangeRef = useRef<TreeItem<TData>[] | null>(null)
  const pendingOnMoveNodeRef = useRef<OnMoveNodeParams<TData> | null>(null)
  const pendingOnVisibilityToggleRef =
    useRef<OnVisibilityToggleParams<TData> | null>(null)
  /** Where a just-moved node landed, so the roving tabindex can follow it. */
  const pendingMoveFocusRef = useRef<number | undefined>(undefined)
  /**
   * The tree as it stood before an optimistically committed move, kept only
   * while an `onDrop` is in flight so a rejection has something to revert to.
   *
   * `draggingTreeData` cannot serve: it is the *preview*, with the node already
   * lifted out. This is the last committed tree, which is what the consumer
   * would have to be handed back.
   */
  const preDropTreeDataRef = useRef<TreeItem<TData>[] | null>(null)
  /**
   * The promise handed to dnd-core to hold the settling phase open, resolved
   * from the effect below once `onDrop` has finished.
   *
   * It cannot be resolved where it is created: the move commits through
   * `setState`, so the params `onDrop` needs only exist after React has run the
   * updater. The effect that already emits `onMoveNode` is where both meet.
   */
  const pendingSettleRef = useRef<PendingSettle | null>(null)

  // State
  const [state, setState] = useState<ReactSortableTreeState<TData>>(() => ({
    draggingTreeData: undefined,
    draggedNode: undefined,
    draggedMinimumTreeIndex: undefined,
    draggedDepth: undefined,
    searchMatches: [],
    searchFocusTreeIndex: undefined,
    dragging: false,
    treeData: treeDataProp,
    ignoreOneTreeUpdate: false,
  }))

  /**
   * Latest props read by event-time code (state updaters, DnD callbacks).
   *
   * Going through a ref is what lets the callbacks below be declared with empty
   * dependency lists. That in turn keeps `wrapSource`/`wrapTarget` memoized on
   * stable values only — otherwise each render produces new component *types*
   * and React unmounts and remounts every row.
   */
  const latestRef = useRef({
    getNodeKey,
    shouldCopyOnOutsideDrop,
    rowDirection,
    onDrop,
    announcements,
  })
  useIsomorphicLayoutEffect(() => {
    latestRef.current = {
      getNodeKey,
      shouldCopyOnOutsideDrop,
      rowDirection,
      onDrop,
      announcements,
    }
  })

  /**
   * Supplies the horizontal intent a keyboard drag has no pointer for. Per tree
   * rather than module-scoped, since two trees each own their own provider and
   * could in principle be dragged in independently.
   */
  const manager = useDragDropManager()
  const announce = useDragDropAnnounce()
  /*
   * `react-hooks/refs` flags `isRtl` for reading a ref inside a function handed
   * to another function during render. It cannot see that these callbacks are
   * only ever invoked from a key event — the same shape as `getDndHandlers`
   * below, and covered by the keyboard drag-and-drop tests.
   */
  // eslint-disable-next-line react-hooks/refs
  const [keyboardDrag] = useState(() =>
    createKeyboardDragController({
      isKeyboardDragging: () => isKeyboardDrag(manager),
      isRtl: () => latestRef.current.rowDirection === 'rtl',
      announce,
      // Through the ref, not captured: the controller is created once and lives
      // as long as the tree, while the prop it reads can change on any render.
      describeDepth: (params) =>
        depthMessage(latestRef.current.announcements, params),
    })
  )
  useEffect(() => keyboardDrag.register(), [keyboardDrag])

  const startDrag = useCallback(
    ({ path }: { path: number[] }) => {
      // Every drag starts with no accumulated depth, whichever device drives it.
      keyboardDrag.reset()
      setState((prevState) => {
        const result = removeNode({
          treeData: prevState.treeData,
          path,
          getNodeKey: latestRef.current.getNodeKey,
        })

        if (!result) {
          return prevState
        }

        const {
          treeData: draggingTreeData,
          node: draggedNode,
          treeIndex: draggedMinimumTreeIndex,
        } = result

        return {
          ...prevState,
          draggingTreeData,
          draggedNode,
          draggedDepth: path.length - 1,
          draggedMinimumTreeIndex,
          dragging: true,
        }
      })
      // `keyboardDrag` is stable, so this stays referentially stable — which is
      // what keeps the wrapped DnD components from being rebuilt.
    },
    [keyboardDrag]
  )

  const moveNode = useCallback(
    ({
      node,
      path: prevPath,
      treeIndex: prevTreeIndex,
      depth,
      minimumTreeIndex,
    }: {
      node: TreeItem<TData>
      path: number[]
      treeIndex: number
      depth: number
      minimumTreeIndex: number
    }) => {
      setState((prevState) => {
        /*
         * Commit the position the preview is showing, not a freshly computed
         * one.
         *
         * The drop target recomputes `minimumTreeIndex` and `depth` from its own
         * row props at drop time — but the last hover already slid the rows, so
         * those props describe the row's *post-preview* position and resolve to
         * where the node came from. A pointer drag hides this because the
         * browser keeps firing `dragover` until the two agree; one hover
         * followed by a drop does not, which is every keyboard drop and every
         * single-event test.
         *
         * `draggedMinimumTreeIndex` / `draggedDepth` are what drew the preview,
         * so using them makes the drop land where the user was shown it would.
         * They are absent for a drag that never hovered a row — an external drop
         * source, or the empty-tree placeholder — and then the drop target's own
         * values are all there is.
         */
        const dropMinimumTreeIndex =
          prevState.draggedMinimumTreeIndex ?? minimumTreeIndex
        const dropDepth = prevState.draggedDepth ?? depth

        const {
          treeData,
          treeIndex,
          path,
          parentNode: nextParentNode,
        } = insertNode({
          treeData: prevState.draggingTreeData!,
          newNode: node,
          depth: dropDepth,
          minimumTreeIndex: dropMinimumTreeIndex,
          expandParent: true,
          getNodeKey: latestRef.current.getNodeKey,
        })

        // What a rejected `onDrop` reverts to. `prevState.treeData` rather than
        // `draggingTreeData`, which is the preview with the node already lifted
        // out. Written here because this updater is the only place the last
        // committed tree is in hand; re-running it produces the same value, so
        // a double invocation under StrictMode is harmless.
        preDropTreeDataRef.current = prevState.treeData

        // Deferred to an effect so the callbacks never fire mid-update
        pendingOnChangeRef.current = treeData
        pendingOnMoveNodeRef.current = {
          treeData,
          node,
          nextPath: path,
          nextTreeIndex: treeIndex,
          prevPath,
          prevTreeIndex,
          nextParentNode,
        }

        return {
          ...prevState,
          treeData,
          draggingTreeData: undefined,
          draggedNode: undefined,
          draggedMinimumTreeIndex: undefined,
          draggedDepth: undefined,
          dragging: false,
        }
      })
    },
    []
  )

  /**
   * Reverts an optimistically committed move after `onDrop` rejected.
   *
   * Mirrors `moveNode`: the internal tree is set directly *and* `onChange` is
   * queued, because the tree is controlled and the consumer holds the copy that
   * actually renders. Handing back the pre-drop data is the whole point — a
   * consumer that only listens to `onChange` still ends up consistent.
   */
  const revertMove = useCallback((snapshot: TreeItem<TData>[]) => {
    setState((prevState) => {
      pendingOnChangeRef.current = snapshot
      return { ...prevState, treeData: snapshot }
    })
  }, [])

  const drop = useCallback(
    (
      dropResult: DropResult<TData>,
      signal: AbortSignal
    ): Promise<void> | void => {
      moveNode(dropResult)

      // No `onDrop` means nothing to wait for, and returning synchronously is
      // what keeps this tree's drop result readable in `end` — see `settleWith`
      // in dnd-manager. Only opt into a settling phase when one is real.
      if (!latestRef.current.onDrop) return

      // Resolved from the effect that emits `onMoveNode`: `moveNode` commits
      // through `setState`, so the params `onDrop` is called with do not exist
      // yet at this point.
      return new Promise<void>((resolve, reject) => {
        pendingSettleRef.current = { resolve, reject, signal }
      })
    },
    [moveNode]
  )

  const endDrag = useCallback(
    (dropResult: DropResult<TData> | null, phase: EndDragPhase) => {
      // The hovered row's replay closure would otherwise outlive the drag.
      keyboardDrag.setReplayHover(undefined)

      // An async drop is still in flight, so there is no result to read yet and
      // nothing to unwind: `drop` has already committed the move optimistically
      // and the settling phase owns what happens next. Falling through here
      // would clear the very state that a rejection needs to revert.
      if (phase.dropped && !dropResult && phase.settling) {
        return
      }

      // Drop was cancelled. Keyed on `didDrop()` rather than on the result being
      // absent, which is also true for the settling window above.
      if (!phase.dropped) {
        setState((prevState) => ({
          ...prevState,
          draggingTreeData: undefined,
          draggedNode: undefined,
          draggedMinimumTreeIndex: undefined,
          draggedDepth: undefined,
          dragging: false,
        }))
      } else if (dropResult && dropResult.treeId !== treeId) {
        // The node was dropped in an external drop target or tree.
        //
        // The `dropResult &&` guard is for the type, not for a reachable state: a
        // synchronous target that returns nothing still yields `{}` here, whose
        // `treeId` is `undefined` and so takes this branch as it always did. A
        // genuinely absent result means the settling case handled above.
        setState((prevState) => {
          const { node, path, treeIndex } = dropResult
          const {
            getNodeKey: currentGetNodeKey,
            shouldCopyOnOutsideDrop: copy,
          } = latestRef.current
          const shouldCopy =
            typeof copy === 'function'
              ? copy({ node, prevTreeIndex: treeIndex, prevPath: path })
              : copy

          // If copying is enabled, a drop outside leaves behind a copy in the
          //  source tree
          const treeData = shouldCopy
            ? changeNodeAtPath({
                treeData: prevState.treeData, // use treeData unaltered by the drag operation
                path,
                newNode: ({ node: copyNode }: { node: TreeItem<TData> }) => ({
                  ...copyNode,
                }), // create a shallow copy of the node
                getNodeKey: currentGetNodeKey,
              })
            : prevState.draggingTreeData || prevState.treeData

          pendingOnChangeRef.current = treeData
          pendingOnMoveNodeRef.current = {
            treeData,
            node,
            nextPath: undefined,
            nextTreeIndex: undefined,
            prevPath: path,
            prevTreeIndex: treeIndex,
            nextParentNode: null,
          }

          return {
            ...prevState,
            treeData,
            draggingTreeData: undefined,
            draggedNode: undefined,
            draggedMinimumTreeIndex: undefined,
            draggedDepth: undefined,
            dragging: false,
          }
        })
      }
    },
    [treeId, keyboardDrag]
  )

  const dragHover = useCallback(
    ({
      node: draggedNode,
      depth: draggedDepth,
      minimumTreeIndex: draggedMinimumTreeIndex,
    }: {
      node: TreeItem<TData>
      depth: number
      minimumTreeIndex: number
    }) => {
      setState((prevState) => {
        // Ignore this hover if it is at the same position as the last hover
        if (
          prevState.draggedDepth === draggedDepth &&
          prevState.draggedMinimumTreeIndex === draggedMinimumTreeIndex
        ) {
          return prevState
        }

        const currentGetNodeKey = latestRef.current.getNodeKey

        // Fall back to the tree data if something is being dragged in from
        //  an external element
        const newDraggingTreeData =
          prevState.draggingTreeData || prevState.treeData

        const addedResult = insertNode({
          treeData: newDraggingTreeData,
          newNode: draggedNode,
          depth: draggedDepth,
          minimumTreeIndex: draggedMinimumTreeIndex,
          expandParent: true,
          getNodeKey: currentGetNodeKey,
        })

        // `insertNode` already knows where it put the node, so take the path
        // from there. This used to re-flatten the entire tree just to read
        // `rows[addedResult.treeIndex].path` — a full traversal on every
        // mousemove. (Only viable since insertNode stopped reporting a bogus
        // single-segment path for nested inserts.)
        const expandedParentPath = addedResult.path

        return {
          ...prevState,
          draggedNode,
          draggedDepth,
          draggedMinimumTreeIndex,
          draggingTreeData: changeNodeAtPath({
            treeData: newDraggingTreeData,
            path: expandedParentPath.toSpliced(-1),
            newNode: ({ node }: { node: TreeItem<TData> }) => ({
              ...node,
              expanded: true,
            }),
            getNodeKey: currentGetNodeKey,
          }),
          // reset the scroll focus so it doesn't jump back
          // to a search result while dragging
          searchFocusTreeIndex: undefined,
          dragging: true,
        }
      })
    },
    []
  )

  const toggleChildrenVisibility = useCallback(
    ({ node: targetNode, path }: { node: TreeItem<TData>; path: number[] }) => {
      setState((prevState) => {
        const treeData = changeNodeAtPath({
          treeData: prevState.treeData,
          path,
          newNode: ({ node }: { node: TreeItem<TData> }) => ({
            ...node,
            expanded: !node.expanded,
          }),
          getNodeKey: latestRef.current.getNodeKey,
        })

        pendingOnChangeRef.current = treeData
        pendingOnVisibilityToggleRef.current = {
          treeData,
          node: targetNode,
          expanded: !targetNode.expanded,
          path,
        }

        // Must return new state to trigger re-render so pending callbacks effect runs
        return {
          ...prevState,
          treeData,
        }
      })
    },
    []
  )

  /**
   * Handed to the wrapped DnD components as one stable object.
   *
   * Seeded with the first render's values so the wrapped components are usable
   * before any effect has run, then refreshed in a layout effect — which lands
   * before any pointer event can reach them.
   */
  const dndHandlersRef = useRef<TreeDndHandlers<TData>>({
    canNodeHaveChildren,
    canDrop,
    maxDepth,
    keyboard: keyboardDrag,
    startDrag,
    endDrag,
    drop,
    dragHover,
  })
  useIsomorphicLayoutEffect(() => {
    dndHandlersRef.current = {
      canNodeHaveChildren,
      canDrop,
      maxDepth,
      keyboard: keyboardDrag,
      startDrag,
      endDrag,
      drop,
      dragHover,
    }
  })
  const getDndHandlers = useCallback(() => dndHandlersRef.current, [])

  /*
   * Wrapped components. Memoized on the renderer, tree id and dnd type only —
   * every other input reaches them through `getDndHandlers`, so their component
   * identity survives a parent re-render and rows are updated, not remounted.
   *
   * `react-hooks/refs` flags these because `getDndHandlers` reads a ref and is
   * passed to a function during render. The lint cannot see that the wrappers
   * only *store* it and call it from drag events; nothing here reads the ref
   * while rendering. Covered by the render-stability and drag-and-drop tests.
   */
  /* eslint-disable react-hooks/refs */
  const nodeContentRenderer = useMemo(
    () => wrapSource(NodeContent, dndType, getDndHandlers),
    [NodeContent, dndType, getDndHandlers]
  )

  const treePlaceholderRenderer = useMemo(
    () => wrapPlaceholder(TreePlaceholder, treeId, dndType, getDndHandlers),
    [treeId, dndType, getDndHandlers]
  )

  const treeNodeRenderer = useMemo(
    () => wrapTarget(TreeNodeRendererBase, treeId, dndType, getDndHandlers),
    [TreeNodeRendererBase, treeId, dndType, getDndHandlers]
  )
  /* eslint-enable react-hooks/refs */

  const getRows = useCallback(
    (rowTreeData: TreeItem<TData>[]) =>
      getFlatDataFromTree({
        ignoreCollapsed: true,
        getNodeKey,
        treeData: rowTreeData,
      }),
    [getNodeKey]
  )

  // Effect events: user callbacks invoked from effects. Wrapping them keeps
  // them out of dependency arrays, so the effects below re-run when their real
  // inputs change rather than on every render.
  const emitChange = useEffectEvent((next: TreeItem<TData>[]) => onChange(next))
  const emitMoveNode = useEffectEvent((params: OnMoveNodeParams<TData>) =>
    onMoveNode?.(params)
  )
  const emitVisibilityToggle = useEffectEvent(
    (params: OnVisibilityToggleParams<TData>) => onVisibilityToggle?.(params)
  )
  const emitDragStateChanged = useEffectEvent(
    (params: OnDragStateChangedParams<TData>) => onDragStateChanged?.(params)
  )

  const runSearch = useEffectEvent(
    (
      searchTreeData: TreeItem<TData>[],
      query: string | undefined,
      focusOffset: number | undefined,
      seekIndex: boolean,
      expand: boolean,
      singleSearch: boolean
    ) =>
      performSearch(
        {
          onChange,
          getNodeKey,
          searchFinishCallback,
          searchQuery: query,
          searchMethod,
          searchFocusOffset: focusOffset,
          onlyExpandSearchedNodes,
        },
        searchTreeData,
        seekIndex,
        expand,
        singleSearch
      )
  )

  /**
   * Says where a moved node ended up.
   *
   * The backend narrates the drag it can see — picked up, moved over row 3 of 8,
   * dropped — but it cannot know that the node is now a child of "Documents" at
   * depth 2, because only the tree knows what the rows mean. Routing this
   * through the backend's own live region keeps app and backend messages in one
   * queue instead of two that talk over each other, and it is a no-op when the
   * provider has no keyboard backend.
   *
   * With an async `onDrop` there are three of these rather than one. Announcing
   * "moved" at commit time and saying nothing when the save fails is a lie a
   * screen-reader user has no way to catch: the rows snap back in silence.
   */
  const announceMove = useEffectEvent((params: OnMoveNodeParams<TData>) => {
    const described = describeMove(params)
    if (described) announce(moveMessage(announcements, 'moved', described))
  })

  /** Said at commit time when a save is still in flight. */
  const announceMovePending = useEffectEvent(
    (params: OnMoveNodeParams<TData>) => {
      const described = describeMove(params)
      if (described) announce(moveMessage(announcements, 'moving', described))
    }
  )

  /**
   * Waits on the consumer's `onDrop` and turns the outcome into the settling
   * phase dnd-core is holding open.
   *
   * The move is already committed when this runs — optimistically, so the tree
   * does not sit frozen under the cursor while a request is in flight. A
   * rejection is what puts it back.
   */
  const runSettle = useEffectEvent(
    (params: OnMoveNodeParams<TData>, settle: PendingSettle) => {
      const snapshot = preDropTreeDataRef.current
      preDropTreeDataRef.current = null

      const persist = onDrop
      if (!persist) {
        settle.resolve()
        return
      }

      // Everything below runs in a later microtask, where an effect event is no
      // longer the right tool — so the message parts, the overrides and
      // `announce` itself are captured here instead of routed back through one.
      const described = describeMove(params)
      const strings = announcements
      void (async () => {
        try {
          await persist(params, settle.signal)
          if (described) {
            announce(moveMessage(strings, 'moved', described))
          }
          settle.resolve()
        } catch (error) {
          // An abort is the tree's own doing — a new drag took the drop-result
          // slot — so it is not a failed save, and reverting would fight the
          // drag already in progress.
          if (!settle.signal.aborted) {
            if (snapshot) revertMove(snapshot)
            if (described) {
              announce(moveMessage(strings, 'moveFailed', described))
            }
          }
          // Rejecting records the reason on `monitor.getDropError()` and hands
          // it to the environment's uncaught-error handling; dnd-core discards
          // an `AbortError` that followed its own abort.
          settle.reject(error)
        }
      })()
    }
  )

  const runLoadLazyChildren = useEffectEvent(
    (lazyTreeData: TreeItem<TData>[]) =>
      loadLazyChildren(
        { onChange, getNodeKey, loadCollapsedLazyChildren },
        lazyTreeData
      )
  )

  // Effect: Execute pending callbacks after state updates
  // This avoids the "Cannot update a component while rendering a different component" warning
  useEffect(() => {
    if (pendingOnChangeRef.current !== null) {
      const nextTreeData = pendingOnChangeRef.current
      pendingOnChangeRef.current = null
      emitChange(nextTreeData)
    }

    if (pendingOnMoveNodeRef.current !== null) {
      const params = pendingOnMoveNodeRef.current
      pendingOnMoveNodeRef.current = null
      emitMoveNode(params)

      const settle = pendingSettleRef.current
      if (settle === null) {
        announceMove(params)
      } else {
        pendingSettleRef.current = null
        announceMovePending(params)
        runSettle(params, settle)
      }

      // Where the node landed, for the roving-tabindex sync below. Recorded
      // rather than applied here: the focus state is declared further down.
      pendingMoveFocusRef.current = params.nextTreeIndex
    }

    // A settle with nothing queued behind it means the commit never happened, so
    // there is nothing to persist. Resolving it matters more than it looks: a
    // promise dnd-core never sees settle leaves the manager in a settling phase
    // for good, and `isSettling()` stays true on every subsequent drag.
    if (pendingSettleRef.current !== null) {
      const orphaned = pendingSettleRef.current
      pendingSettleRef.current = null
      preDropTreeDataRef.current = null
      orphaned.resolve()
    }

    if (pendingOnVisibilityToggleRef.current !== null) {
      const params = pendingOnVisibilityToggleRef.current
      pendingOnVisibilityToggleRef.current = null
      emitVisibilityToggle(params)
    }
  })

  // Effect: Initial mount - load lazy children and perform initial search
  useEffect(() => {
    runLoadLazyChildren(treeDataProp)

    startSearchTransition(() => {
      const searchResult = runSearch(
        treeDataProp,
        searchQuery,
        searchFocusOffset,
        true,
        true,
        false
      )
      setState((prev) => ({
        ...prev,
        searchMatches: searchResult.searchMatches,
        searchFocusTreeIndex: searchResult.searchFocusTreeIndex,
        ignoreOneTreeUpdate: searchResult.newTreeData
          ? true
          : prev.ignoreOneTreeUpdate,
      }))
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Effect: Handle tree data changes from props
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      return
    }

    if (prevTreeDataRef.current === treeDataProp) {
      return
    }
    prevTreeDataRef.current = treeDataProp

    // Check if we should ignore this update (set by search expanding nodes)
    let shouldSearch = true
    setState((prevState) => {
      if (prevState.ignoreOneTreeUpdate) {
        shouldSearch = false
        return {
          ...prevState,
          treeData: treeDataProp,
          ignoreOneTreeUpdate: false,
        }
      }

      return {
        ...prevState,
        treeData: treeDataProp,
        draggingTreeData: undefined,
        draggedNode: undefined,
        draggedMinimumTreeIndex: undefined,
        draggedDepth: undefined,
        dragging: false,
      }
    })

    // Outside of setState to avoid "Cannot call startTransition while rendering"
    if (shouldSearch) {
      runLoadLazyChildren(treeDataProp)

      startSearchTransition(() => {
        const searchResult = runSearch(
          treeDataProp,
          searchQuery,
          searchFocusOffset,
          false,
          false,
          false
        )
        setState((prev) => ({
          ...prev,
          searchMatches: searchResult.searchMatches,
          searchFocusTreeIndex: undefined,
        }))
      })
    }
  }, [treeDataProp, searchQuery, searchFocusOffset])

  // Effect: Handle deferred search query changes
  useEffect(() => {
    const queryChanged =
      prevDeferredSearchQueryRef.current !== deferredSearchQuery
    const offsetChanged =
      prevDeferredSearchFocusOffsetRef.current !== deferredSearchFocusOffset

    prevDeferredSearchQueryRef.current = deferredSearchQuery
    prevDeferredSearchFocusOffsetRef.current = deferredSearchFocusOffset

    if (!queryChanged && !offsetChanged) {
      return
    }

    startSearchTransition(() => {
      const searchResult = runSearch(
        state.treeData,
        deferredSearchQuery,
        deferredSearchFocusOffset,
        true,
        true,
        // A changed focus offset alone steps through existing matches rather
        // than re-running the whole search.
        !queryChanged
      )
      setState((prev) => ({
        ...prev,
        searchMatches: searchResult.searchMatches,
        searchFocusTreeIndex: searchResult.searchFocusTreeIndex,
        ignoreOneTreeUpdate: searchResult.newTreeData
          ? true
          : prev.ignoreOneTreeUpdate,
      }))
    })
  }, [deferredSearchQuery, deferredSearchFocusOffset, state.treeData])

  // Effect: Call onDragStateChanged when dragging state changes
  useEffect(() => {
    if (prevDraggingRef.current === state.dragging) {
      return
    }

    prevDraggingRef.current = state.dragging
    emitDragStateChanged({
      isDragging: state.dragging,
      draggedNode: state.draggedNode,
    })
  }, [state.dragging, state.draggedNode])

  const {
    searchMatches,
    searchFocusTreeIndex,
    draggedNode,
    draggedDepth,
    draggedMinimumTreeIndex,
    draggingTreeData,
    treeData: stateTreeData,
  } = state

  const treeData = draggingTreeData || stateTreeData
  const rowDirectionClass = rowDirection === 'rtl' ? 'rst__rtl' : undefined
  // `aria-setsize` for depth-1 rows, which have no parentNode to count.
  const rootNodeCount = treeData.length

  const { rows, swapFrom, swapLength } = useMemo(() => {
    if (draggedNode && draggedMinimumTreeIndex !== undefined) {
      const addedResult = insertNode({
        treeData,
        newNode: draggedNode,
        depth: draggedDepth!,
        minimumTreeIndex: draggedMinimumTreeIndex,
        expandParent: true,
        getNodeKey,
      })

      const computedSwapFrom = addedResult.treeIndex
      const computedSwapLength = 1 + getDescendantCount({ node: draggedNode })
      return {
        rows: slideRows(
          getRows(addedResult.treeData),
          computedSwapFrom,
          draggedMinimumTreeIndex,
          computedSwapLength
        ),
        swapFrom: computedSwapFrom,
        swapLength: computedSwapLength,
      }
    }

    return {
      rows: getRows(treeData),
      swapFrom: undefined,
      swapLength: undefined,
    }
  }, [
    treeData,
    draggedNode,
    draggedDepth,
    draggedMinimumTreeIndex,
    getNodeKey,
    getRows,
  ])

  /* ------------------------------------------------------------------ *
   * Keyboard navigation (WAI-ARIA tree pattern)
   *
   * One row at a time is tabbable — a roving tabindex — and the arrow keys
   * move that focus. `focusedRowIndex` is an index into `rows`, so it tracks
   * the visible order rather than the tree structure.
   *
   * These are plain functions, not `useEffectEvent`: effect events may not be
   * passed as props, and these are attached to the container element. They are
   * only ever handed to a DOM node, so a fresh identity per render costs
   * nothing.
   * ------------------------------------------------------------------ */
  const containerRef = useRef<HTMLDivElement>(null)
  const [focusedRowIndex, setFocusedRowIndex] = useState(0)
  // Set when navigation should move DOM focus, cleared once it lands. Focus
  // cannot be applied immediately to a row virtua has not mounted yet.
  const pendingFocusRef = useRef<number | undefined>(undefined)

  const activeRowIndex = Math.min(focusedRowIndex, Math.max(rows.length - 1, 0))

  const focusRow = (index: number) => {
    if (rows.length === 0) return
    const clamped = Math.max(0, Math.min(index, rows.length - 1))
    setFocusedRowIndex(clamped)
    pendingFocusRef.current = clamped
    listRef.current?.scrollToIndex(clamped, { align: 'nearest' })
  }

  /**
   * Keeps the roving tabindex on the row that moved.
   *
   * A keyboard drop leaves focus on the dragged row's handle while the row lands
   * somewhere new, so `focusedRowIndex` still points at the old position and the
   * tab stop and the focused element end up on different rows — after which the
   * next arrow key acts somewhere unexpected. This is the same failure the
   * container's `onFocus` sync exists to prevent, and it cannot help here because
   * focus never actually moved.
   *
   * Only when focus is inside the tree, so a pointer drag does not pull the tab
   * stop around while the user is somewhere else entirely.
   */
  // Runs after every render, like the focus effect below, because a move lands a
  // render after the drop. It cannot loop: the ref is cleared before the state
  // is set, so the next render returns at the first line.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- honoured by oxlint too; the `oxlint-` prefix silenced only one of the two linters
  useEffect(() => {
    const moved = pendingMoveFocusRef.current
    if (moved === undefined) return
    pendingMoveFocusRef.current = undefined
    if (!containerRef.current?.contains(document.activeElement)) return
    setFocusedRowIndex(moved)
  })

  // Runs after every render so a row scrolled into view can still be focused.
  useEffect(() => {
    const target = pendingFocusRef.current
    if (target === undefined) return
    const element = containerRef.current?.querySelector<HTMLElement>(
      `[data-rst-row="${CSS.escape(String(target))}"]`
    )
    if (element) {
      pendingFocusRef.current = undefined
      element.focus()
    }
  })

  /**
   * Keeps the roving tabindex aligned with wherever focus actually went —
   * clicking a row, tabbing in, or a consumer calling `.focus()` — so the arrow
   * keys always act on the row the user is really on.
   */
  const handleFocus = (event: React.FocusEvent) => {
    const rowElement =
      event.target instanceof HTMLElement
        ? event.target.closest<HTMLElement>('[data-rst-row]')
        : null
    const index = Number(rowElement?.dataset['rstRow'])
    if (Number.isSafeInteger(index)) {
      setFocusedRowIndex(index)
    }
  }

  const toggleRow = (row: FlatDataItem<TData>) =>
    toggleChildrenVisibility({ node: row.node, path: row.path as number[] })

  /** ArrowRight in ltr: open a closed node, else step into its first child. */
  const expandOrEnter = (index: number, row: FlatDataItem<TData>) => {
    if (!hasRevealableChildren(row.node)) return
    if (row.node.expanded === true) {
      focusRow(index + 1)
    } else {
      toggleRow(row)
    }
  }

  /** ArrowLeft in ltr: close an open node, else move up to its parent. */
  const collapseOrLeave = (index: number, row: FlatDataItem<TData>) => {
    if (hasRevealableChildren(row.node) && row.node.expanded === true) {
      toggleRow(row)
      return
    }
    const depth = row.path.length
    const parentIndex = rows.findLastIndex(
      (candidate, i) => i < index && candidate.path.length < depth
    )
    if (parentIndex !== -1) {
      focusRow(parentIndex)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!keyboardNavigation || rows.length === 0) return
    // The keyboard drag backend listens on the document in the capture phase and
    // calls `preventDefault` on the keys it claims, so by the time this bubble
    // handler runs a claimed key has already done its job. Without this check,
    // space on a drag handle would pick the row up *and* toggle its children,
    // and an arrow key mid-drag would move both the hover and the focused row.
    if (event.defaultPrevented) return
    if (isFromTextEntry(event.target)) return

    const { key } = event
    const isToggleKey = key === 'Enter' || key === ' '
    if (!NAVIGATION_KEYS.has(key) && !isToggleKey) return

    const index = Math.min(activeRowIndex, rows.length - 1)
    const row = rows[index]
    // `activeRowIndex` is clamped to the row count, so this only misses if the
    // rows changed underneath the handler. Return without preventDefault so
    // the key keeps its native behaviour.
    if (!row) return
    // Right means "deeper" in ltr and "shallower" in rtl.
    const deeper = rowDirection === 'rtl' ? 'ArrowLeft' : 'ArrowRight'

    switch (key) {
      case 'ArrowDown': {
        focusRow(index + 1)
        break
      }
      case 'ArrowUp': {
        focusRow(index - 1)
        break
      }
      case 'Home': {
        focusRow(0)
        break
      }
      case 'End': {
        focusRow(rows.length - 1)
        break
      }
      case 'ArrowLeft':
      case 'ArrowRight': {
        if (key === deeper) {
          expandOrEnter(index, row)
        } else {
          collapseOrLeave(index, row)
        }
        break
      }
      default: {
        // Enter or Space
        if (hasRevealableChildren(row.node)) toggleRow(row)
      }
    }

    // Only keys handled above reach this point, so nothing else is swallowed.
    event.preventDefault()
  }
  // Get indices for rows that match the search conditions
  const matchKeys = useMemo(() => {
    const keys: Record<TreeKey, number> = {}
    for (const [i, { path }] of searchMatches.entries()) {
      const lastKey = path.at(-1)
      if (lastKey !== undefined) {
        keys[lastKey] = i
      }
    }
    return keys
  }, [searchMatches])

  /*
   * Renders one row.
   *
   * Everything below the key and the two search flags lives in `TreeRow`, which
   * is memoized — see its comment. This function stays deliberately thin: work
   * done here runs for every visible row on every render, work done in `TreeRow`
   * runs only for the rows whose props actually changed.
   */
  const renderRow = useCallback(
    (
      row: FlatDataItem<TData>,
      {
        listIndex,
        prevRow,
        swapFrom: rowSwapFrom,
        swapDepth,
        swapLength: rowSwapLength,
      }: {
        listIndex: number
        prevRow: FlatDataItem<TData> | undefined
        swapFrom: number | undefined
        swapDepth: number | undefined
        swapLength: number | undefined
      }
    ) => {
      const { node, path } = row
      const nodeKey = path.at(-1)
      const isSearchMatch =
        nodeKey !== undefined && Object.hasOwn(matchKeys, nodeKey)
      const isSearchFocus =
        isSearchMatch && matchKeys[nodeKey] === searchFocusOffset

      // React's row identity. A caller-supplied `getNodeKey` is meant to be
      // stable, so its value is the key; the *default* one is `treeIndex`, which
      // shifts whenever anything above the row is inserted, removed or moved —
      // as a key that hands one node's row instance to another node. Fall back
      // to the node's own identity in that case. See `node-identity.ts`.
      const rowKey = usesDefaultNodeKey ? rowIdentity(node) : nodeKey

      return (
        <TreeRow
          key={rowKey}
          row={row}
          prevRow={prevRow}
          listIndex={listIndex}
          isActiveRow={listIndex === activeRowIndex}
          isSearchMatch={isSearchMatch}
          isSearchFocus={isSearchFocus}
          swapFrom={rowSwapFrom}
          swapDepth={swapDepth}
          swapLength={rowSwapLength}
          rootNodeCount={rootNodeCount}
          scaffoldBlockPxWidth={scaffoldBlockPxWidth}
          treeId={treeId}
          rowDirection={rowDirection}
          rowHeight={rowHeight}
          toggleChildrenVisibility={toggleChildrenVisibility}
          generateNodeProps={generateNodeProps}
          canDrag={canDrag}
          TreeNodeRenderer={treeNodeRenderer}
          NodeContentRenderer={nodeContentRenderer}
        />
      )
    },
    [
      treeNodeRenderer,
      nodeContentRenderer,
      matchKeys,
      searchFocusOffset,
      generateNodeProps,
      canDrag,
      scaffoldBlockPxWidth,
      treeId,
      rowDirection,
      rowHeight,
      toggleChildrenVisibility,
      activeRowIndex,
      rootNodeCount,
      usesDefaultNodeKey,
    ]
  )

  // Seek to the focused search result if there is one specified
  useEffect(() => {
    if (searchFocusTreeIndex !== undefined) {
      listRef?.current?.scrollToIndex(searchFocusTreeIndex, {
        smooth: true,
        align: 'center',
      })
    }
  }, [searchFocusTreeIndex, listRef])

  let containerStyle = style
  let list
  if (rows.length === 0) {
    const Placeholder = treePlaceholderRenderer
    list = (
      // eslint-disable-next-line react-hooks/static-components
      <Placeholder treeId={treeId} drop={drop}>
        <PlaceholderContent />
      </Placeholder>
    )
  } else {
    containerStyle = { height: '100%', ...containerStyle }

    list = (
      <VList
        id="vlist"
        // Scroll container and per-row wrappers both sit between the tree and
        // its treeitems; neither may appear in the accessibility tree.
        role="none"
        ref={listRef}
        style={innerStyle}
        data={rows}
        item={PresentationalItem}>
        {(item, index) =>
          renderRow(item, {
            listIndex: index,
            prevRow: rows[index - 1],
            swapFrom,
            swapDepth: draggedDepth,
            swapLength,
          })
        }
      </VList>
    )
  }

  return (
    <div
      ref={containerRef}
      // The scroll container sits between this element and the rows, so the
      // tree role goes here and the intervening elements are presentational.
      role="tree"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      // The tab stop is the active row, per the roving-tabindex half of the
      // ARIA tree pattern; -1 keeps the container programmatically focusable
      // without adding a second stop.
      tabIndex={-1}
      dir={rowDirection === 'rtl' ? 'rtl' : 'ltr'}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      className={classnames(
        'rst__tree',
        className || '',
        rowDirectionClass ?? ''
      )}
      style={containerStyle}>
      {list}
    </div>
  )
}

export const SortableTreeWithoutDndContext = <TData = UnknownNodeData,>(
  props: ReactSortableTreeProps<TData>
): React.JSX.Element => {
  return (
    <DndContext.Consumer>
      {({ dragDropManager }) =>
        dragDropManager === undefined ? undefined : (
          <ReactSortableTreeInner
            {...props}
            dragDropManager={dragDropManager}
          />
        )
      }
    </DndContext.Consumer>
  )
}

/**
 * The parts of `KeyboardBackendOptions` a tree can safely hand on.
 *
 * Both are about *words*, which is exactly the part a consumer needs and the part
 * this library has no business fixing. Everything else in
 * `KeyboardBackendOptions` is about behaviour, and the two behavioural options
 * `withTreeKeyboard` sets are what make the backend tree-shaped.
 */
export type TreeKeyboardOptions = Pick<
  KeyboardBackendOptions,
  'announcements' | 'describeNode'
>

/**
 * Adds tree-shaped keyboard drag-and-drop to a pointer backend.
 *
 * `SortableTree` applies this to `HTML5Backend` already. Reach for it when you
 * supply your own provider — with `SortableTreeWithoutDndContext`, or to swap in
 * `TouchBackend` — since a bare pointer backend has no keyboard gesture at all
 * and dropping it is silent.
 *
 * ```tsx
 * <DndProvider backend={withTreeKeyboard(TouchBackend)}>
 *   <SortableTreeWithoutDndContext … />
 * </DndProvider>
 * ```
 *
 * A pointer backend no longer has to be *one* backend: `composeBackends` from
 * `@nosferatu500/dnd-core` runs several at once, so mouse and touch need no
 * feature detection between them. Hand the composite in like any other base —
 * `withTreeKeyboard(composeBackends(HTML5Backend, TouchBackend))` — since
 * `withKeyboard` flattens a composite rather than nesting one. The
 * `Advanced/TouchSupport` story does exactly this.
 *
 * Two options make it tree-shaped:
 *
 * - `gridNavigation({ columns: 1 })` rather than the default document-order
 *   model. A tree is a single column, so up and down should step between rows
 *   while left and right stay put — and staying put is what leaves them free to
 *   mean *depth*, matching the pointer, where horizontal movement is the only
 *   thing that nests a node. Document-order navigation maps left and right onto
 *   previous and next row, spending the horizontal keys on the vertical job and
 *   leaving no way to nest at all.
 * - `onNavigate` takes left and right for depth, and lets up and down through.
 *
 * `applyAriaAttributes` is deliberately left alone. The backend never overwrites
 * an attribute an element already carries, so the drag handle's own `tabIndex`
 * wins and the tree keeps a single tab stop — while a custom renderer that says
 * nothing still gets a focusable, described drag source rather than one that
 * cannot be picked up at all.
 *
 * `announcements` and `describeNode` are forwarded, which is how the backend's
 * own strings get localised:
 *
 * ```tsx
 * const backend = withTreeKeyboard(HTML5Backend, {
 *   announcements: {
 *     instructions: 'Appuyez sur Espace pour saisir cet élément.',
 *     pickUp: ({ source }) => `${source} saisi.`,
 *   },
 * })
 * ```
 *
 * Only those two, on purpose. `getNextTarget` and `onNavigate` are what make the
 * backend tree-shaped — see above — so they are not overridable: a consumer who
 * replaced either would silently lose depth control or the vertical arrows.
 * Anything that specific is better served by calling `withKeyboard` directly.
 *
 * The tree's *own* strings — where a node landed, what depth a key produced —
 * are a separate, per-tree concern and travel on the `announcements` prop
 * instead. Both halves are needed to see no English at all.
 *
 * Call it once, at module scope. A new backend factory identity tears down and
 * rebuilds the entire drag-and-drop manager.
 */
export const withTreeKeyboard = (
  base: Parameters<typeof withKeyboard>[0],
  options?: TreeKeyboardOptions
): ReturnType<typeof withKeyboard> =>
  withKeyboard(base, {
    ...options,
    getNextTarget: gridNavigation({ columns: 1 }),
    onNavigate: treeOnNavigate,
  })

const KeyboardHTML5Backend = withTreeKeyboard(HTML5Backend)

export const SortableTree = <TData = UnknownNodeData,>(
  props: ReactSortableTreeProps<TData>
): React.JSX.Element => {
  return (
    <DndProvider backend={KeyboardHTML5Backend}>
      <SortableTreeWithoutDndContext {...props} />
    </DndProvider>
  )
}
